import React from 'react';
import fr from 'facet-rest';
import { Link } from 'react-router-dom';
import {
  householdEditRecordUri,
  householdTimelineUri,
  householdBlueprintUri,
  householdNewPrintUri,
  householdPlansUri,
  householdSecureFilesUri,
  taskBoardUri,
  accountSupportUri,
} from 'Consts/advisorUris';

import { noop } from 'Helpers/PropTypes';
import Button from 'Components/UI/Button';
import { makeStyles } from '@material-ui/core/styles';
import './index.css';
import { withHousehold } from 'Context/HouseholdContext';
import relationships from 'Consts/Relationships';
import PropTypes from 'prop-types';
import featureEnabled, { hasRelationship, pick, pickImplementation } from 'Helpers/Affordances';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useUserContext from 'Context/UserContext';
import LaunchWorkspaceButton from 'Components/UI/LaunchWorkspaceButton';
import { buildAdvisorLink, buildPageLink } from 'Views/Page/Content/Templates';
import { advisorPageMessages, advisorPage } from 'Consts/Strings';
import SimplePicker from 'Helpers/SimplePicker';
import { useMissionControlURL } from 'Hooks/useMissionControlURL';
import { useBalanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';

const useStyles = makeStyles(() => ({
  linkButton: {
    minWidth: '0px',
    justifyContent: 'start',
  },
  workspace: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
}));
// TODO: can't inline comments; insert actual links where appropriate
const QuickLinks = ({
  householdId,
  toggle,
  linkColor,
}) => {
  const classes = useStyles();
  const { householdResource } = useSimpleHousehold();
  const { userResource } = useUserContext();
  const taskboardUriParams = pickImplementation(userResource, {
    [relationships.default]: {
      householdId,
      link: householdResource?.relationship(relationships.simpleActionItems).link('related').url(),
    },
  });
  const missionControlURL = useMissionControlURL();
  const balanceSheetPageURL = useBalanceSheetPageURL();

  return (
    <div className="QuickLinks">
      <h4 className="title">Quick Links</h4>
      <ul>
        <li>
          <Button
            className={classes.linkButton}
            component={Link}
            padding="none"
            color={linkColor}
            onClick={toggle}
            size="small"
            to={householdSecureFilesUri(householdId)}
          >
            {'Files'}
          </Button>
        </li>
        <li>
          <Button
            className={classes.linkButton}
            component={Link}
            onClick={toggle}
            color={linkColor}
            padding="none"
            size="small"
            to={householdEditRecordUri(householdId)}
          >
            {'Edit Record'}
          </Button>
        </li>
        <li>
          <Button
            className={classes.linkButton}
            component={Link}
            onClick={toggle}
            color={linkColor}
            padding="none"
            size="small"
            to={householdBlueprintUri(householdId)}
          >
            {'Blueprint'}
          </Button>
        </li>
        <li>
          <Button
            className={classes.linkButton}
            component={Link}
            onClick={toggle}
            color={linkColor}
            padding="none"
            size="small"
            to={householdNewPrintUri(householdId)}
          >
            {'Snapshot'}
          </Button>
        </li>
        <li>
          <Button
            className={classes.linkButton}
            component={Link}
            onClick={toggle}
            color={linkColor}
            padding="none"
            size="small"
            to={householdPlansUri(householdId)}
          >
            {'Plans'}
          </Button>
        </li>
        <li>
          <Button
            className={classes.linkButton}
            padding="none"
            component={Link}
            color={linkColor}
            onClick={toggle}
            size="small"
            to={householdTimelineUri(householdId)}
          >
            {'Timeline'}
          </Button>
        </li>
        <li>
          <Button
            className={classes.linkButton}
            component={Link}
            onClick={toggle}
            color={linkColor}
            padding="none"
            size="small"
            to={taskBoardUri(taskboardUriParams)}
          >
            {'Action Items'}
          </Button>
        </li>
        <li>
          <Button
            className={classes.linkButton}
            variant="text"
            padding="none"
            component={Link}
            color={linkColor}
            size="small"
            to={balanceSheetPageURL}
          >
            {
              pick(householdResource, {
                [fr.relationships.householdBalanceSheetPage.name]: 'Balance Sheet',
              }, 'Accounts')
            }
          </Button>
        </li>
        {missionControlURL
          ? (
            <li>
              <Button
                className={classes.linkButton}
                component={Link}
                padding="none"
                color={linkColor}
                onClick={toggle}
                size="small"
                to={missionControlURL}
              >
                {'Mission Control'}
              </Button>
            </li>
          ) : null}
        {userResource
          && userResource.relationship(fr.relationships.plaidLinkSessions.name) && (
            <li>
              <Button
                className={classes.linkButton}
                component={Link}
                variant="text"
                padding="none"
                color={linkColor}
                size="small"
                to={accountSupportUri(householdId, userResource.relationship(fr.relationships.plaidLinkSessions.name).link('related').copy().equal('householdId', householdId)
                  .url())}
              >
                {'Account Support'}
              </Button>
            </li>)
        }
        {hasRelationship(householdResource, fr.relationships.messagesPage.name)
          ? (
            <li>
              <Button
                className={classes.linkButton}
                component={Link}
                variant="text"
                padding="none"
                color={linkColor}
                size="small"
                to={() => buildPageLink(
                  householdResource?.relationship(fr.relationships.messagesPage.name).link('related'),
                  advisorPageMessages,
                  { householdId: householdResource.attr('householdId') },
                )}
              >
                {'Messages'}
              </Button>
            </li>) : null }
        {hasRelationship(householdResource, fr.relationships.transitionsDashboardPage.name)
          ? (
            <li>
              <Button
                className={classes.linkButton}
                component={Link}
                variant="text"
                padding="none"
                color={linkColor}
                size="small"
                to={() => buildPageLink(
                  householdResource?.relationship(fr.relationships.transitionsDashboardPage.name).link('related'),
                  advisorPage,
                )}
              >
                {'Transitions Dashboard'}
              </Button>
            </li>) : null }
        <SimplePicker
          resource={householdResource}
          relationship={fr.relationships.legacyDashboard.name}
          hasRelationship={() => {
            return (
              <li>
                <Button
                  component={Link}
                  variant="text"
                  padding="none"
                  color={linkColor}
                  size="small"
                  to={() => {
                    const link = householdResource.relationship(fr.relationships.legacyDashboard.name).link('related').url();

                    return buildAdvisorLink(link);
                  }}
                >
                  {'Household Dashboard'}
                </Button>
              </li>
            );
          }}
        />
      </ul>
      {featureEnabled(userResource, relationships.workspaces) && (
        <div className={classes.workspace}>
          <LaunchWorkspaceButton />
        </div>
      )}
    </div>
  );
};

QuickLinks.propTypes = {
  householdId: PropTypes.number.isRequired,
  toggle: PropTypes.func,
  linkColor: PropTypes.string,
};

QuickLinks.defaultProps = {
  toggle: noop,
  linkColor: 'primary',
};

export default withHousehold(QuickLinks);
